import { Node } from '@tiptap/core';
import { blockNodeSpec, dataAttribute } from './block';
import { blockViews, initialView } from './block_views';

// Live event: a card for one upcoming livestream or premiere of the post's podcast. Saved as a bare block
// placeholder with the event's id as data-live-event-id; Newsletters::RenderBlocks renders the card from the
// LiveEvent at display time (Block::Newsletter::LiveEventComponent), so its title, time and state follow the
// event. The editor chrome lives in dashboard/shared/tiptap/_live_event_editor.html.erb and its preview comes
// from the same server component.
//
// The page passes data-tiptap-block-capabilities-value.live_event = { post_id, events: [{ id, title, kind,
// label }] }: the podcast's (or network's) upcoming events. The slash item is hidden when there are none.

const PREVIEW_DELAY_MS = 300;

export const LiveEventBlock = Node.create({
  name: 'live_event',

  ...blockNodeSpec('live_event', {
    attributes: {
      liveEventId: dataAttribute('liveEventId'),
    },
  }),

  addOptions() {
    return {
      templates: null,         // { editor_html } — injected by the tiptap controller from the server template
      capabilities: null,      // { post_id, events } — injected by the tiptap controller
      fetchRenderedHtml: null, // async (attrs) => string | null — injected by the tiptap controller
      onRenderedHtmlFetched: null,
    };
  },

  addNodeView() {
    return ({ node, getPos, editor }) => {
      const extension = this;
      const events = extension.options.capabilities?.events || [];
      let currentNode = node;
      let previewTimer = null;
      let previewRequest = 0;

      const dom = document.createElement('div');
      dom.setAttribute('data-live-event-node', '');
      dom.innerHTML = extension.options.templates?.editor_html
        || '<div class="my-3 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-500">Live event</div>';

      const select = dom.querySelector('[data-field="event"]');
      const preview = dom.querySelector('[data-preview]');
      const missing = dom.querySelector('[data-missing]');

      const selectedId = () => String(currentNode.attrs.liveEventId || '');
      const isUpcoming = id => events.some(event => String(event.id) === id);

      // One option per upcoming event ("Title · Thu, Mar 5, 7:00 PM EST"), plus the saved event when it is no
      // longer upcoming, so the select never renders blank for a block that already has one.
      const fillOptions = () => {
        if (!select) return;
        select.innerHTML = '';
        select.add(new Option('Choose an event…', ''));
        events.forEach(event => select.add(new Option(`${event.title} · ${event.label}`, String(event.id))));
        const saved = selectedId();
        if (saved && !isUpcoming(saved)) select.add(new Option('Event no longer upcoming', saved));
      };

      const canFinish = () => Boolean(selectedId());

      let views = null;

      const syncFromNode = () => {
        if (select && select !== document.activeElement) select.value = selectedId();
        views?.refresh();
      };

      const showPreview = html => {
        if (preview) preview.innerHTML = html || '';
        missing?.classList.toggle('hidden', Boolean(html));
      };

      const schedulePreview = () => {
        if (!extension.options.fetchRenderedHtml || views?.view !== 'preview') return;
        clearTimeout(previewTimer);
        previewTimer = setTimeout(async () => {
          const request = ++previewRequest;
          const html = await extension.options.fetchRenderedHtml({ liveEventId: selectedId() });
          if (request === previewRequest) showPreview(html);
        }, PREVIEW_DELAY_MS);
      };

      const setAttr = (attr, value) => {
        const pos = typeof getPos === 'function' ? getPos() : null;
        if (pos === null || pos === undefined) return;
        editor.view.dispatch(editor.state.tr.setNodeMarkup(pos, undefined, { ...currentNode.attrs, [attr]: value }));
      };

      select?.addEventListener('change', e => setAttr('liveEventId', e.target.value));

      dom.querySelector('[data-remove]')?.addEventListener('mousedown', e => {
        e.preventDefault();
        const pos = typeof getPos === 'function' ? getPos() : null;
        if (pos === null || pos === undefined) return;
        const current = editor.state.doc.nodeAt(pos);
        if (current) editor.chain().focus().deleteRange({ from: pos, to: pos + current.nodeSize }).run();
      });

      // The preview has real links; never navigate away from the editor.
      preview?.addEventListener('click', e => e.preventDefault());

      fillOptions();
      views = blockViews(dom, {
        canFinish,
        onChange: view => { if (view === 'preview') schedulePreview(); },
      });
      syncFromNode();
      views.setView(initialView({ hasContent: canFinish() }));

      return {
        dom,
        // Using the select must not reach ProseMirror (keys would act on the node).
        stopEvent: event => Boolean(event.target?.closest?.('input, select, textarea, button')),
        ignoreMutation: () => true,
        update(updatedNode) {
          if (updatedNode.type.name !== 'live_event') return false;
          currentNode = updatedNode;
          syncFromNode();
          schedulePreview();
          return true;
        },
        destroy() {
          clearTimeout(previewTimer);
        },
      };
    };
  },

  addCommands() {
    return {
      insertLiveEvent: (attrs = {}) => ({ chain }) =>
        chain().insertContent({ type: this.name, attrs }).run(),
    };
  },
});

function liveEventAvailable(editor) {
  const extension = editor.extensionManager.extensions.find(ext => ext.name === 'live_event');
  return (extension?.options.capabilities?.events || []).length > 0;
}

// Only offered when the podcast has an upcoming livestream or premiere to point at.
export const liveEventSlashItem = {
  title: 'Live event',
  description: 'A card for an upcoming livestream or premiere, with a Join button',
  icon: '●',
  group: 'Blocks',
  keywords: ['live', 'livestream', 'stream', 'premiere', 'event', 'join'],
  canInsert: editor => liveEventAvailable(editor),
  command: ({ editor, range }) => {
    editor.chain().focus().deleteRange(range).insertLiveEvent().run();
  },
};
